import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_TO_CATEGORY = {
  instagram: "social_media_posts",
  facebook: "social_media_posts",
  linkedin: "social_media_posts",
  email: "campaigns",
  text: "campaigns",
  print: "materials_created",
  in_person: "events",
  other: "partner_promotion",
};

export const LogActivityModal = ({
  asset, folder, categories, channels, mlos = [], authHeader, onClose, onSaved,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const [channel, setChannel] = useState(asset ? "instagram" : "in_person");
  const [category, setCategory] = useState(asset ? "social_media_posts" : "events");
  const [title, setTitle] = useState(asset ? asset.filename : "");
  const [note, setNote] = useState("");
  const [url, setUrl] = useState("");
  const [occurredOn, setOccurredOn] = useState(today);
  const [mloSlug, setMloSlug] = useState(mlos[0]?.slug || "");
  const [busy, setBusy] = useState(false);

  const pickChannel = (c) => {
    setChannel(c);
    setCategory(CHANNEL_TO_CATEGORY[c] || category);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Give it a short title so you recognise it later.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/bdm/activity`,
        {
          category,
          channel,
          title: title.trim(),
          note: note.trim(),
          url: url.trim(),
          occurred_on: occurredOn,
          mlo_slug: mloSlug || undefined,
          asset_id: asset?.id,
          asset_filename: asset?.filename,
          folder_id: folder?.id,
          folder_name: folder?.name,
        },
        { headers: authHeader() },
      );
      toast.success("Logged — it'll be waiting at month end.");
      onSaved?.(data);
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not log that.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl border border-linen-border shadow-xl max-h-[92vh] overflow-y-auto"
        data-testid="bdm-log-modal"
      >
        <div className="sticky top-0 bg-white border-b border-linen-border px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-teal">Log activity</p>
            <h3 className="font-heading font-medium text-xl text-navy mt-0.5">
              {asset ? "You used this — nice." : "What did you do?"}
            </h3>
            <p className="text-xs text-ink-500 mt-1">
              Twenty seconds now saves you rebuilding the month from memory later.
            </p>
          </div>
          <button type="button" onClick={onClose} data-testid="bdm-log-close" className="text-ink-400 hover:text-navy">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <span className="text-xs uppercase tracking-widest text-ink-500">Where did it go?</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {Object.entries(channels || {}).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => pickChannel(key)}
                  data-testid={`bdm-log-channel-${key}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                    channel === key
                      ? "bg-navy text-white border-navy"
                      : "bg-white text-navy border-linen-border hover:border-teal"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">What was it?</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Rate-drop graphic for the Maple St listing"
              data-testid="bdm-log-title"
              className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Report category</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="bdm-log-category"
                className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
              >
                {Object.entries(categories || {}).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Date</span>
              <input
                type="date"
                value={occurredOn}
                onChange={(e) => setOccurredOn(e.target.value)}
                data-testid="bdm-log-date"
                className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
              />
            </label>
          </div>

          {mlos.length > 1 && (
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">For which officer?</span>
              <select
                value={mloSlug}
                onChange={(e) => setMloSlug(e.target.value)}
                data-testid="bdm-log-mlo"
                className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-white focus:border-teal focus:outline-none"
              >
                {mlos.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.name}</option>
                ))}
              </select>
            </label>
          )}

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">
              Link <span className="normal-case tracking-normal text-ink-400">(optional — the post, listing, or event page)</span>
            </span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://"
              data-testid="bdm-log-url"
              className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-ink-500">
              Notes <span className="normal-case tracking-normal text-ink-400">(optional)</span>
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Who saw it, how it went, anything worth remembering."
              data-testid="bdm-log-note"
              className="mt-1 w-full rounded-xl border border-linen-border p-2.5 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
            />
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 rounded-full text-navy hover:bg-linen">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="bdm-log-submit"
              className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2 rounded-full bg-lime text-navy hover:brightness-95 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save to my log
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogActivityModal;
